import { ADMIN_API_BASE_URL } from "../config";
import { handleApiResponse } from "../utils/commonfunction";
import { makeApiRequest, addAppTypeToParams, addAppTypeToBody } from "../utils/apiHelper";

export interface WithdrawalRequest {
  id: string;
  user_id: string;
  user_email: string;
  user_name: string;
  user_mobile: string;
  requested_amount: number;
  tds_amount: number;
  net_amount: number;
  bank_account_details: {
    account_holder_name: string;
    bank_name: string;
    account_number: string;
    ifsc_code: string;
    masked_account: string;
  };
  request_date: string;
  status: string;
  rejection_reason?: string;
  admin_notes?: string;
  processed_by?: string;
  processed_at?: string;
  payout_transaction_id?: string;
  user_details?: {
    kyc_status: string;
    withdrawable_balance: number;
    total_winnings: number;
    account_status: string;
  };
}

export interface WithdrawalQueueResponse {
  withdrawals: WithdrawalRequest[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface WithdrawalAnalytics {
  status_breakdown: {
    [key: string]: {
      count: number;
      total_amount: number;
    };
  };
  recent_withdrawals_7_days: number;
  total_pending_amount: number;
}

export interface WithdrawalApproval {
  admin_notes?: string;
}

export interface WithdrawalRejection {
  reason: string;
  admin_notes?: string;
}


class WithdrawalService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  }

  private async makeRequest(endpoint: string, options: RequestInit = {}) {
    const response = await fetch(`${ADMIN_API_BASE_URL}${endpoint}`, {
      ...options,
      headers: {
        ...this.getAuthHeaders(),
        ...options.headers,
      },
    });
    return handleApiResponse(response)
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.detail || `HTTP error! status: ${response.status}`
      );
    }

    return response.json();
  }
  async fetchWithdrawals(
    page: number = 1,
    perPage: number = 20,
    statusFilter: string = "",
    searchTerm?: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<any> {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      status_filter: statusFilter,
      ...(searchTerm ? { search: searchTerm } : {}),
      ...(dateFrom ? { date_from: dateFrom } : {}),
      ...(dateTo ? { date_to: dateTo } : {}),
    });

    const paramsWithAppType = addAppTypeToParams(params);

    return makeApiRequest(`/withdrawal-queue?${paramsWithAppType}`, {
      method: "GET",
    });
  }
  async getWithdrawalQueue(
    params: {
      page?: number;
      per_page?: number;
      status_filter?: string;
      search?: string;
      date_from?: string;
      date_to?: string;
    } = {}
  ): Promise<WithdrawalQueueResponse> {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== "") {
        searchParams.append(key, value.toString());
      }
    });

    const paramsWithAppType = addAppTypeToParams(searchParams);
    return makeApiRequest(`/withdrawal-queue?${paramsWithAppType.toString()}`);
  }

  async getWithdrawalDetails(withdrawalId: string): Promise<WithdrawalRequest> {
    return makeApiRequest(`/withdrawals/${withdrawalId}`);
  }

  async approveWithdrawal(
    withdrawalId: string,
    approvalData: WithdrawalApproval
  ): Promise<any> {
    const bodyWithAppType = addAppTypeToBody(approvalData);
    return makeApiRequest(`/withdrawals/${withdrawalId}/approve`, {
      method: "POST",
      body: JSON.stringify(bodyWithAppType),
    });
  }

  async rejectWithdrawal(
    withdrawalId: string,
    rejectionData: WithdrawalRejection
  ): Promise<any> {
    const bodyWithAppType = addAppTypeToBody(rejectionData);
    return makeApiRequest(`/withdrawals/${withdrawalId}/reject`, {
      method: "POST",
      body: JSON.stringify(bodyWithAppType),
    });
  }

  async getWithdrawalAnalytics(): Promise<WithdrawalAnalytics> {
    return makeApiRequest("/withdrawal-analytics");
  }
}

export const withdrawalService = new WithdrawalService();
export default withdrawalService;
